/**
 * Undoing a half-finished handheld screen (Ingest project, a new subject in
 * Ingest export), Segment 5.
 *
 * The links are undone for everyone (unlinkSubjectFromProject is a write
 * every handheld writer has, and is a no-op on a link that is not there).
 * Erasing what the attempt made (deleteSubject, deleteProject) is an admin's
 * only, because it uses up the number; for anyone else those items stay on
 * the list and the message says which. The message never claims more than
 * the calls showed: a link that could not be undone, or an erase that
 * failed, is said plainly.
 *
 * A link whose call failed may still have gone through (the answer was lost),
 * so it is undone too (`pending`); for an activation the tethered code is not
 * cleared then (the server would clear a code the subject already had).
 */
import { pad2, pad3 } from "./handheldNaming.js";

const listOf = (subjects) => subjects.map((s) => pad3(s.number)).join(", ");

function madeLabel(subjects, projectSerial) {
  const parts = [];
  if (subjects.length) parts.push(`${subjects.length === 1 ? "subject" : "subjects"} ${listOf(subjects)}`);
  if (Number.isFinite(projectSerial)) parts.push(`project ${pad2(projectSerial)}`);
  return parts.join(" and ");
}

/** What stays after a rig operator's failed commit (exported for tests). */
export function leftoversNote({ subjects = [], projectSerial = null, unlinkFailed = false }) {
  const made = madeLabel(subjects, projectSerial);
  if (!made && !unlinkFailed) return null;
  if (!made) return "Not every link could be undone: check this project's subjects on the list.";
  const one = subjects.length + (Number.isFinite(projectSerial) ? 1 : 0) === 1;
  const [they, them] = one ? ["it stays", "it"] : ["they stay", "them"];
  if (unlinkFailed) return `This attempt made ${made}; ${they} on the list. Not every link could be undone: check ${them}; an admin can fix or erase ${them}.`;
  return `This attempt made ${made}; ${they} on the list, empty. Activate or reuse ${them} to try again; an admin can erase ${them}.`;
}

/**
 * Ingest project: undo `linked` (+ `pending`) on `projectId`, then, for an
 * admin, erase the subjects and the project this attempt made. Returns the
 * message to add to the error, or null.
 *   linked:   [{ subjectId, clearCapturedByCode? }] links that went through
 *   pending:  { subjectId } | null, the link whose call failed
 *   alreadyLinked: subject ids that were in the project before this attempt
 *             (joining an existing project): their links are never undone
 *   created:  { subjects: [{ id, number }], project: { id, number } | null }
 */
export async function undoProjectCommit(api, { jobId, projectId, linked, pending = null, alreadyLinked = [], created, canErase }) {
  let unlinkFailed = false;
  const before = new Set(alreadyLinked);
  const links = (pending ? [...linked, { subjectId: pending.subjectId }] : linked)
    .filter((l) => !before.has(l.subjectId));
  if (projectId) {
    for (const link of links) {
      try {
        await api.unlinkSubjectFromProject({
          jobId, subjectId: link.subjectId, projectId,
          ...(link.clearCapturedByCode ? { clearCapturedByCode: link.clearCapturedByCode } : {}),
        });
      } catch {
        unlinkFailed = true;
      }
    }
  }
  const project = created.project ?? null;
  if (!canErase) {
    return leftoversNote({ subjects: created.subjects, projectSerial: project?.number ?? null, unlinkFailed });
  }
  const notErased = [];
  for (const s of created.subjects) {
    try { await api.deleteSubject({ jobId, subjectId: s.id }); } catch { notErased.push(`subject ${pad3(s.number)}`); }
  }
  if (project) {
    try { await api.deleteProject({ jobId, projectId: project.id }); } catch { notErased.push(`project ${pad2(project.number)}`); }
  }
  const unlinkNote = unlinkFailed ? "Not every link could be undone: check this project's subjects on the list." : null;
  const eraseNote = notErased.length ? `Could not erase ${notErased.join(", ")}: check ${notErased.length === 1 ? "it" : "them"} on the list.` : null;
  return [unlinkNote, eraseNote].filter(Boolean).join(" ") || null;
}

/**
 * Ingest export, a new subject in a project: the subject was made, its link
 * failed (or its answer was lost). Undo the link; an admin also erases the
 * subject. Returns the message to add to the error, or null.
 */
export async function undoNewSubjectInProject(api, { jobId, projectId, subject, canErase }) {
  let unlinked = true;
  try { await api.unlinkSubjectFromProject({ jobId, subjectId: subject.id, projectId }); } catch { unlinked = false; }
  const which = Number.isFinite(subject.number) ? `Subject ${pad3(subject.number)}` : "The new subject";
  if (canErase && unlinked) {
    try { await api.deleteSubject({ jobId, subjectId: subject.id }); return null; } catch { /* said below */ }
    return `${which} was made and could not be erased: check it on the list.`;
  }
  if (!unlinked) return `${which} was made; it may or may not be in this project: check it on the list.`;
  return `${which} was made but is not in this project; it stays on the job's list, empty (Ingest project can activate it). An admin can erase it.`;
}
